import { axiosInstance } from "@/lib/axios";
import { hasAuthCookie } from "@/lib/cookies";
import type { AuthConfig, LoginInput, LoginResponse, SessionResponse, User } from "./types";
import { refreshCsrfToken } from "@/lib/csrf";
import { showErrorToast } from "@/lib/toast";

export const login = async (input: LoginInput): Promise<LoginResponse> => {
  const { data } = await axiosInstance.post<LoginResponse>(
    "/api/auth/login",
    input,
  );
  // Refresh CSRF token after login to get a fresh token for the new session
  await refreshCsrfToken();
  return data;
};

export const logout = async (): Promise<void> => {
  await axiosInstance.post("/api/auth/logout");
  // Refresh CSRF token after logout
  await refreshCsrfToken();
};

export const fetchSession = async (): Promise<User | null> => {
  // Skip API call if no auth cookie exists (user never logged in or session expired)
  if (!hasAuthCookie()) {
    // Only show toast if not on public auth pages (register, verify-email)
    const pathname = window.location.pathname;
    const isPublicAuthPage = pathname === "/register" || pathname.startsWith("/verify-email/");
    if (!isPublicAuthPage) {
      showErrorToast("Session expired. Please log in again.");
    }
    return null;
  }

  const { data } =
    await axiosInstance.get<SessionResponse>("/api/auth/session");
  return data.user;
};

export interface Session {
  tokenId: string;
  createdAt: string;
  expiresAt: string;
  ipAddress?: string;
  userAgent?: string;
  isCurrent?: boolean;
}

export type SessionsResponse = {
  success: boolean;
  sessions: Session[];
};

export const getSessions = async (): Promise<Session[]> => {
  const { data } =
    await axiosInstance.get<SessionsResponse>("/api/auth/sessions");
  return data.sessions;
};

export const revokeSession = async (tokenId: string): Promise<void> => {
  await axiosInstance.delete(`/api/auth/sessions/${tokenId}`);
};

export const getAuthConfig = async (): Promise<AuthConfig> => {
  const { data } = await axiosInstance.get<AuthConfig>("/api/auth/config");
  return data;
};
